import { createAsyncThunk } from "@reduxjs/toolkit";
import { showAlert } from "../slices/features/alert";
import { getBaseUrl } from "../selectors/features";
import { UserProfileService } from "@root/services/user-profile-change";
import {
  UPLOAD_FILE,
  USER_PROFILE,
  UPDATE_TOUR_COMPLETION,
  FETCH_TOUR_GUIDES,
} from "../action-types/userProfile";
import { TOAST_VARIANT } from "@root/constants/common";
import { t } from "i18next";
import { getLoggedInUser } from "../selectors/entities";
import type { TourGuideItem } from "@root/constants/tour-guide";

const userProfileService = new UserProfileService();

interface UserProfilePayload {
  payload: {
    fullname?: string;
    bio?: string;
    selectedFile?: File;
    ai_data_retention?: boolean;
    default_theme?: string;
    preferred_language?: string;
    avatar?: string;
  };
  onSuccess: () => void;
  onError: () => void;
  disableAlert?: boolean;
}

export const userProfile = createAsyncThunk<
  TObject,
  UserProfilePayload,
  IActionOptions
>(USER_PROFILE, async (requestPayload, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const userProfile: any = getLoggedInUser(thunkAPI.getState());
  const { payload, disableAlert, onError, onSuccess } = requestPayload;

  const {
    fullname,
    bio,
    selectedFile,
    ai_data_retention,
    default_theme,
    preferred_language,
    avatar,
  } = payload;

  const formData: FormData = new FormData();

  if (ai_data_retention !== undefined)
    formData.append("ai_data_retention", ai_data_retention.toString());
  if (default_theme) formData.append("default_theme", default_theme);
  if (preferred_language)
    formData.append("preferred_language", preferred_language);
  if (bio) formData.append("bio", bio);
  if (fullname) formData.append("fullname", fullname);
  if (selectedFile) formData.append("file", selectedFile);
  if (avatar !== undefined) formData.append("avatar", avatar);

  const { data, error } = await userProfileService.userProfile(
    baseUrl,
    formData,
  );

  if (error) {
    onError();
    thunkAPI.dispatch(
      showAlert({
        message:
          data?.message ||
          data?.userMessage ||
          data?.code ||
          "Something went wrong",
        type: TOAST_VARIANT.ERROR,
      }),
    );
    return thunkAPI.rejectWithValue(data || {});
  }

  onSuccess();

  if (!disableAlert) {
    thunkAPI.dispatch(
      showAlert({
        message: t("common:profile.profileUpdate"),
        type: TOAST_VARIANT.SUCCESS,
      }),
    );
  }

  const updatedUser = {
    ...userProfile,
    user: {
      ...userProfile.user,
      ...(selectedFile && { avatar: data }),
      ...(fullname && { fullname }),
      ...(preferred_language && { preferred_language }),
      ...(default_theme && { default_theme }),
      ...(typeof ai_data_retention === "boolean" && { ai_data_retention }),
      ...(bio && { bio }),
    },
  };

  return thunkAPI.fulfillWithValue(updatedUser);
});

interface UploadFilePayload {
  payload: any;
  pid: number;
  onSuccess: (data: any, actionType: string) => void;
  onError: (err: any, actionType: string, res?: any) => void;
}

export const uploadFile = createAsyncThunk<
  TObject,
  UploadFilePayload,
  IActionOptions
>(UPLOAD_FILE, async (requestPayload, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const { onError, onSuccess, payload, pid } = requestPayload;
  const { data, error } = await userProfileService.uploadFile(
    baseUrl,
    payload,
    pid,
  );

  if (error) {
    onError(error, UPLOAD_FILE, data);
    return thunkAPI.rejectWithValue(data || {});
  }

  onSuccess(data, UPLOAD_FILE);
  return thunkAPI.fulfillWithValue(data);
});

interface UpdateTourCompletionPayload {
  payload: { tourGuides: TourGuideItem[] };
  onSuccess?: () => void;
  onError?: () => void;
}

export const fetchTourGuides = createAsyncThunk<
  { tourGuides: TourGuideItem[] },
  void,
  IActionOptions
>(FETCH_TOUR_GUIDES, async (_, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const { data, error } = await userProfileService.fetchTourGuides(baseUrl);

  if (error) {
    return thunkAPI.rejectWithValue((data as TObject) || {});
  }

  const d = data as { rows?: TourGuideItem[]; tourGuides?: TourGuideItem[] };
  const tourGuides = (
    Array.isArray(d?.rows) ? d.rows : (d?.tourGuides ?? [])
  ) as TourGuideItem[];
  return { tourGuides };
});

export const updateTourCompletion = createAsyncThunk<
  TObject,
  UpdateTourCompletionPayload,
  IActionOptions
>(UPDATE_TOUR_COMPLETION, async (requestPayload, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const user = getLoggedInUser(thunkAPI.getState()) as any;
  const existing = (user?.tourGuides ?? []) as TourGuideItem[];
  const { payload, onSuccess, onError } = requestPayload;

  const alreadyExists = payload.tourGuides.every((p) =>
    existing.some((e) => e.type === p.type && e.isDone === true),
  );
  if (alreadyExists) {
    if (onSuccess) onSuccess();
    return thunkAPI.fulfillWithValue({
      tourData: { tourGuides: existing },
    });
  }

  const { data, error } = await userProfileService.submitTourCompletion(
    baseUrl,
    payload,
  );

  if (error) {
    if (onError) onError();
    return thunkAPI.rejectWithValue((data as TObject) || {});
  }

  if (onSuccess) onSuccess();
  const tourGuides = ((data as TObject)?.tourGuides ??
    payload.tourGuides) as TourGuideItem[];
  return thunkAPI.fulfillWithValue({ tourData: { tourGuides } });
});
